import {DataTable, Tab, TabsNavigation, Text} from '@/components-v2'
import {useProductListBulkEdit} from '@/hooks/useProductListBulkEdit'
import {useProductListBusinessGroupTabs} from '@/hooks/useProductListBusinessGroupTabs'
import {useProductListColumns} from '@/hooks/useProductListColumns'
import {useProductListTableV2} from '@/hooks/useProductListTableV2'
import {useMemo, useState} from 'react'
import {ColumnSettingsModal} from './ColumnSettingsModal'
import {ProductListActions} from './ProductListActions'
import {ProductListEditProvider} from './ProductListEditContext'
import {ProductListTableHeader} from './ProductListTableHeader'
import { useSelector } from 'react-redux'
import { selectCountryCode } from '@/store/slices/countryCodeSlice'

export const ProductListTabContent = () => {
  const [isSettingsModalOpen, setSettingsModalOpen] = useState(false)

  const {hasBusinessGroups, businessGroupTabs, selectedTabIndex, setSelectedTabIndex, selectedBusinessGroup} =
    useProductListBusinessGroupTabs()

  const {columns, visibleColumnKeys, availableColumns, onApplyColumns} = useProductListColumns()
  const countryCode = useSelector(selectCountryCode)

  const {
    tableData,
    isFetching,
    pageIndex,
    pageSize,
    totalRowsCount,
    manualPagination,
    manualSorting,
    manualFiltering,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    resetFilterButtonOptions,
    onTableReady,
    rowSelectionStateUpdater,
    initialRowSelection,
    isComparing,
    canCompare,
    toggleCompareMode,
    canDownload,
    isDownloading,
    onDownloadClick,
    selectedRows,
    resetAll,
    resetKey,
    selectionResetKey,
    clearSelection,
  } = useProductListTableV2(undefined, selectedBusinessGroup, visibleColumnKeys, columns)

  const {
    isEditMode,
    editedValues,
    isSavingEdits,
    hasMissingRequiredFields,
    missingRequiredFieldColumns,
    hasValidationErrors,
    onEnterEditMode,
    onEditValueChange,
    onCancelEdit,
    onSaveEdit,
  } = useProductListBulkEdit({
    selectedRows,
    onSaved: clearSelection,
    selectionResetKey,
    clearSelection,
  })

  // Only the first row of a multi-row selection stays editable; the rest are frozen and
  // just mirror its edits (see UNIFORM semantics in useProductListBulkEdit).
  const frozenProductIds = useMemo(
    () => new Set(selectedRows.length > 1 ? selectedRows.slice(1).map(row => row.productId) : []),
    [selectedRows]
  )

  const editContextValue = useMemo(
    () => ({isEditMode, editedValues, onEditValueChange, frozenProductIds}),
    [isEditMode, editedValues, onEditValueChange, frozenProductIds]
  )

  const changedRowsCount = Object.keys(editedValues).length

  const handleTabChange = newIndex => {
    if (newIndex === selectedTabIndex) return
    setTimeout(() => {
      setSelectedTabIndex(newIndex)
      resetAll()
    }, 0)
  }

  const customHeader = (
    <ProductListTableHeader
      resetFilterButtonOptions={resetFilterButtonOptions}
      isComparing={isComparing}
      canCompare={canCompare}
      onToggleCompare={toggleCompareMode}
      onSettingsClick={() => setSettingsModalOpen(true)}
      canDownload={canDownload}
      isDownloading={isDownloading}
      onDownloadClick={onDownloadClick}
    />
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-lg bg-[var(--color-background-layer-01)] p-6">
        <Text headingLevel={5}>Product list</Text>
        {hasBusinessGroups && (
          <TabsNavigation selectedIndex={selectedTabIndex} onUpdate={handleTabChange}>
            {businessGroupTabs.map(({id, label}) => (
              <Tab key={id} label={label} />
            ))}
          </TabsNavigation>
        )}
        <ProductListEditProvider value={editContextValue}>
          <DataTable
            key={`${visibleColumnKeys.join(',')}-${selectedBusinessGroup}-${resetKey}`}
            columns={columns}
            data={tableData}
            isLoading={isFetching}
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalRowsCount={totalRowsCount}
            onPageIndexChange={onPageIndexChange}
            onRowsPerPageChange={onRowsPerPageChange}
            manualPagination={manualPagination}
            manualSorting={manualSorting}
            manualFiltering={manualFiltering}
            sortingHandler={sortingHandler}
            onColumnFiltersChange={onColumnFiltersChange}
            resetFilterButtonOptions={resetFilterButtonOptions}
            selection="multiple"
            onTableReady={onTableReady}
            rowSelectionStateUpdater={rowSelectionStateUpdater}
            initialRowSelection={initialRowSelection}
            showTableHeader={false}
            customHeader={customHeader}
          />
        </ProductListEditProvider>
        <ColumnSettingsModal
          showModal={isSettingsModalOpen}
          onCloseModal={() => setSettingsModalOpen(false)}
          columns={availableColumns}
          visibleColumnKeys={visibleColumnKeys}
          onApply={onApplyColumns}
        />
      </div>
      <ProductListActions
        isEditMode={isEditMode}
        isSaving={isSavingEdits}
        changedRowsCount={changedRowsCount}
        hasMissingRequiredFields={hasMissingRequiredFields}
        missingRequiredFieldColumns={missingRequiredFieldColumns}
        hasValidationErrors={hasValidationErrors}
        countryCode={countryCode}
        onEdit={onEnterEditMode}
        onCancel={onCancelEdit}
        onSave={onSaveEdit}
        selectionCount={selectedRows.length}
      />
    </div>
  )
}
